import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Settings, X } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import {
  readConsent, saveConsent, detectRegion, hasGPCSignal,
  getVisitorId, COOKIE_CATEGORIES, requiresConsentBanner
} from '@/utils/cookieUtils';

export default function CookieConsentManager({ showFloatingButton = true }) {
  const [showBanner, setShowBanner] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [categories, setCategories] = useState({
    necessary: true,
    functional: true,
    analytics: true,
    advertising: true
  });
  const [region, setRegion] = useState('OTHER');

  useEffect(() => {
    // Small delay so the component mounts fully before checking cookies
    const timer = setTimeout(() => {
      const detectedRegion = detectRegion();
      setRegion(detectedRegion);

      // Handle GPC signal for California visitors — auto opt-out of advertising
      if (hasGPCSignal()) {
        const existing = readConsent();
        if (!existing || existing.advertising !== false) {
          const newState = saveConsent({
            necessary: true,
            functional: existing?.functional ?? true,
            analytics: existing?.analytics ?? true,
            advertising: false
          });
          recordConsent('gpc_opt_out', detectedRegion, newState);
        }
        setShowBanner(false);
        return;
      }

      if (requiresConsentBanner()) {
        setShowBanner(true);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const recordConsent = async (action, rgn, state) => {
    const accepted = Object.keys(state).filter(k => k !== 'version' && k !== 'ts' && state[k] === true);
    const rejected = Object.keys(state).filter(k => k !== 'version' && k !== 'ts' && state[k] === false);
    try {
      await base44.functions.invoke('recordConsentEvent', {
        action,
        region: rgn || region,
        consent_version: state.version,
        categories_accepted: accepted,
        categories_rejected: rejected,
        gpc_signal_detected: hasGPCSignal(),
        page_url: window.location.pathname,
        visitor_id: getVisitorId()
      });
    } catch (e) {
      console.warn('Consent logging failed (non-critical):', e.message);
    }
  };

  const handleAcceptAll = () => {
    const state = saveConsent({ necessary: true, functional: true, analytics: true, advertising: true });
    recordConsent('accept_all', region, state);
    setShowBanner(false);
    setShowPanel(false);
  };

  const handleRejectAll = () => {
    const state = saveConsent({ necessary: true, functional: false, analytics: false, advertising: false });
    recordConsent('reject_all', region, state);
    setShowBanner(false);
    setShowPanel(false);
  };

  const handleSaveCustom = () => {
    const state = saveConsent(categories);
    recordConsent('customize', region, state);
    setShowBanner(false);
    setShowPanel(false);
  };

  const openPreferences = () => {
    const existing = readConsent();
    if (existing) {
      setCategories({
        necessary: true,
        functional: existing.functional ?? true,
        analytics: existing.analytics ?? true,
        advertising: existing.advertising ?? true
      });
    }
    setShowPanel(true);
    setShowBanner(false);
  };

  // Expose openPreferences globally so footer can call it
  useEffect(() => {
    window.__openCookiePreferences = openPreferences;
    return () => { delete window.__openCookiePreferences; };
  }, []);

  return (
    <>
      {/* Persistent privacy settings button — always available to reopen consent choices */}
      {showFloatingButton && !showBanner && !showPanel && (
        <button
          onClick={openPreferences}
          aria-label="Privacy settings"
          title="Privacy settings"
          className="fixed bottom-4 left-4 z-[9998] flex items-center gap-1.5 rounded-full bg-card border border-border shadow-lg px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:shadow-xl transition-all"
        >
          <ShieldCheck className="w-4 h-4 text-primary" />
          <span className="hidden sm:inline">Privacy</span>
        </button>
      )}

      {/* Main consent banner */}
      <AnimatePresence>
        {showBanner && !showPanel && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 left-0 right-0 z-[9999] p-4 bg-card border-t border-border shadow-2xl"
            role="dialog"
            aria-label="Cookie consent"
            aria-modal="false"
          >
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Privacy choices for cookies and site analytics</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This site uses necessary technologies to run securely and, with your permission, analytics tools to improve our service.{' '}
                    <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={openPreferences} className="gap-1.5">
                  <Settings className="w-3.5 h-3.5" /> Customize
                </Button>
                <Button size="sm" variant="outline" onClick={handleRejectAll}>
                  Reject non-essential
                </Button>
                <Button size="sm" onClick={handleAcceptAll}>
                  Accept all
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preference panel modal */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-foreground/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-label="Cookie preferences"
            aria-modal="true"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <h2 className="text-base font-semibold">Cookie Preferences</h2>
                </div>
                <button onClick={() => setShowPanel(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-xs text-muted-foreground">
                  Manage your privacy choices below. You can change these at any time via the "Privacy Settings" link in the footer.
                </p>

                {Object.entries(COOKIE_CATEGORIES).map(([key, cfg]) => (
                  <div key={key} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{cfg.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{cfg.description}</p>
                    </div>
                    <div className="shrink-0 mt-0.5">
                      {cfg.alwaysOn ? (
                        <span className="text-xs text-secondary font-medium">Always on</span>
                      ) : (
                        <button
                          role="switch"
                          aria-checked={categories[key]}
                          onClick={() => setCategories(prev => ({ ...prev, [key]: !prev[key] }))}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            categories[key] ? 'bg-primary' : 'bg-muted-foreground/30'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                              categories[key] ? 'translate-x-4' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {region === 'CA' || region === 'US' ? (
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                    California residents: You have the right to opt out of the sale or sharing of personal information.
                    Disabling "Advertising / Sharing" above exercises that right.
                    We also honor Global Privacy Control (GPC) browser signals automatically.
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 p-5 border-t border-border">
                <Button variant="outline" size="sm" onClick={handleRejectAll} className="flex-1">
                  Reject all
                </Button>
                <Button size="sm" onClick={handleSaveCustom} className="flex-1">
                  Save preferences
                </Button>
                <Button size="sm" onClick={handleAcceptAll} className="flex-1">
                  Accept all
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}